import type { ReactNode } from "react";
import { RightOutlined } from "@ant-design/icons";
import { tintBackground } from "./catalogColors";

/**
 * Мрежата с chips на десктоп: равни колони, gap 4px — кутийките са подредени
 * в колони с еднаква ширина. Къщичката и стрелката назад са КЛЕТКИ от същата
 * мрежа (над каталога къщичката е HomeChip с „Всички", стрелката и панелът
 * над прегледа — CHIP_NAV_CELL). CHIP_GRID_BASE е без колоните — редът над
 * каталога взима колкото се съберат (CHIP_GRID, мин. 108px), панелът над
 * прегледа е с фиксирани 3.
 */
export const CHIP_GRID_BASE = "grid gap-1 flex-1 min-w-0";
/**
 * Икон-бутон (къщичка / стрелка назад) като КЛЕТКА от мрежата: пълни
 * ширината на колоната, 40px висок като chips-овете, неутрален вид.
 */
export const CHIP_NAV_CELL =
  "h-10 rounded-[10px] border bg-pos-panel border-pos-line text-pos-dim grid place-items-center cursor-pointer transition-colors hover:border-pos-accent/40";
export const CHIP_GRID = `${CHIP_GRID_BASE} grid-cols-[repeat(auto-fill,minmax(108px,1fr))]`;

interface ColorChipProps {
  /** Цветът на групата/станцията от базата; null = неутрален chip. */
  color: string | null;
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  /** Компактен вариант (мобилният dropdown на групите). */
  compact?: boolean;
  /** Станции: неутрална кутийка с точка в цвета (не оцветена цялата). */
  dot?: boolean;
  /**
   * Група с подгрупи: малък шеврон „›" в десния край на кутийката подсказва,
   * че клик върху нея отваря следващо ниво, а не само филтрира. Листовете
   * са без него.
   */
  hasChildren?: boolean;
}

/**
 * Chip на група/станция — ЕДНА форма навсякъде в „Маркиране" (редът над
 * каталога и филтърът над прегледа): кутийката е оцветена в цвета си (лек
 * фон + рамка), избраната е плътна в същия цвят с бял текст. Без цвят —
 * неутралните pos класове с акцент за избраната. Цветът е от данните →
 * inline style. На десктоп chips-овете стоят в grid с равни колони
 * (CHIP_GRID) — кутийката запълва клетката при фиксирана височина 40px,
 * дълго име минава на два реда и се реже с многоточие след втория; в
 * мобилния dropdown (flex-wrap) ширината е по съдържанието, един ред.
 */
export const ColorChip = ({
  color,
  active,
  onClick,
  children,
  compact = false,
  dot = false,
  hasChildren = false,
}: ColorChipProps) => {
  const base = compact
    ? "relative inline-flex items-center justify-center gap-1.5 min-w-0 whitespace-nowrap text-[12.5px] font-semibold px-3 py-2 rounded-[10px] border cursor-pointer transition-[background,border-color,color]"
    : // Десктоп: фиксирана височина 40px (с бордъра); дълго име минава на
      // два реда със стегнато междуредие (18px), след втория — многоточие
      "relative inline-flex items-center justify-center gap-1.5 min-w-0 h-10 text-base leading-[18px] font-semibold rounded-[10px] border cursor-pointer transition-[background,border-color,color]";
  const neutral = active
    ? "bg-pos-accent text-white border-pos-accent"
    : "bg-pos-card text-pos-soft border-pos-ctl hover:border-pos-accent/40";
  const colored = active ? "text-white" : "text-pos-ink hover:brightness-95";
  const paintBox = !!color && !dot;
  const multiWord = typeof children === "string" && /\s/.test(children.trim());
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      // Десктоп: 6px отстъп от двете страни, съдържанието е центрирано; при
      // станциите точката е на 6px (gap) пред текста и се центрира с него.
      // Шевронът на група с подгрупи е абсолютно вдясно, затова текстът
      // получава по-голям десен отстъп, за да не минава под него
      className={`${base} ${compact ? "" : hasChildren ? "pl-1.5 pr-5" : "px-1.5"} ${paintBox ? colored : neutral}`}
      style={
        paintBox
          ? active
            ? { background: color, borderColor: color }
            : {
                background: tintBackground(color, 22),
                borderColor: `color-mix(in srgb, ${color} 55%, transparent)`,
              }
          : undefined
      }
    >
      {dot && color && (
        // Цветът е от данните → inline style
        <span
          aria-hidden
          className={`w-2 h-2 rounded-full shrink-0 ${active ? "ring-2 ring-white/60" : ""}`}
          style={{ background: color }}
        />
      )}
      <span
        // Една дума, която не се събира → многоточие (без чупене по средата);
        // две и повече думи → пренасяне на втори ред, после многоточие
        className={
          compact
            ? "truncate"
            : multiWord
              ? "line-clamp-2 break-words text-center"
              : "truncate min-w-0 max-w-full text-center"
        }
      >
        {children}
      </span>
      {hasChildren && (
        // Подсказка „има подгрупи": на десктоп е закачен за десния ръб на
        // клетката (текстът остава центриран), в компактния dropdown —
        // след текста. Цветът е този на текста (бял при активна)
        <RightOutlined
          aria-hidden
          className={`text-[10px] opacity-70 ${
            compact ? "shrink-0" : "absolute right-1.5 top-1/2 -translate-y-1/2"
          }`}
        />
      )}
    </button>
  );
};
